import { showOnlineExclusiveFeatureFlag } from "@utils/default";
import { currencyFormatter } from "@utils/formatedPrice";
import { contentfulImageLoader } from "@utils/imageLoader";
import { transparentPixel } from "@utils/string";
import { useFormatter, useLocale, useTranslations } from "next-intl";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useContext, useEffect, useMemo, useState } from "react";

import CardTags from "@/components/AutogeneratedCarousel/CardTags";
import Button, { ButtonSize, ButtonTheme, ButtonVariant } from "@/components/ui/Button/Button";
import CurrencyIcon from "@/components/ui/CurrencyIcon/CurrencyIcon";
import Icon from "@/components/ui/Icon/Icon";
import { projectlocale } from "@/constants/locale";
import { UserContext } from "@/context/UserContext";
import { propertyCardClick } from "@/dataLayer/dataLayer";
import useBreakPoint from "@/hooks/useBreakpoint";
import useMediaQuery from "@/hooks/useMediaQuery";
import { useProjectUrl } from "@/hooks/useProjectUrl";
import useResponsiveLabel from "@/hooks/useResponsiveLabel";
import { ProjectCardListProp } from "@/types";
import cn from "@/utils/cn";

import "./ProjectCardList.css";

const formatBedroomValue = (value: any) => {
  if (!value) return value;

  if (value.includes("STD")) {
    const parts = value.split("-");
    const stdIndex = parts.findIndex((part: any) => part.includes("STD"));
    if (stdIndex > 0) {
      const stdPart = parts.splice(stdIndex, 1)[0];
      parts.unshift(stdPart);
      return parts.join("-");
    }
  }
  return value;
};

export default function ProjectCardList({
  item,
  customClass,
  displayMapPin = false,
  onPinSelected,
  finalUrl,
  hideLocationIcon,
  projectResultPage = false,
  communitiesData,
}: ProjectCardListProp) {
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const { currency } = useContext(UserContext);
  const t = useTranslations();
  const format = useFormatter();
  const router = useRouter();
  const locale = useLocale();
  const [language, region] = locale.split("-");
  const { isMd } = useBreakPoint("md");
  const formattedPrice = currencyFormatter(item.price || 0, currency, format, true, true);

  const fromLabel = useResponsiveLabel(t("project.mobile.from", { 0: formattedPrice }), t("project.desktop.from", { 0: formattedPrice }));

  // Communities matching and creating a URL - Start
  const finalUrlForCommunity = useProjectUrl({ communitiesData: communitiesData || [], item, locale });
  // console.log(
  //   "projectResultPage ? finalUrl : finalUrlForCommunity",
  //   projectResultPage ? finalUrl : finalUrlForCommunity,
  //   finalUrl,
  //   finalUrlForCommunity,
  //   item,
  // );
  // Communities matching and creating a URL - End
  const getTranslatedData = (id: string, lang: string | "", data: any) => {
    const translation = projectlocale.find((item) => item.id === id);

    if (translation) {
      // Helper to ensure we get a string, not an object
      const getVal = (field: any, fallback: string) => {
        if (!field) return fallback;
        // If the field is an object (e.g. {ar: "...", ru: "..."}), get the lang key
        if (typeof field === "object" && field !== null) {
          return field[lang] || field["en"] || fallback;
        }
        // If it's already a string, return it
        return field;
      };

      return {
        name: getVal(translation.name, data.title || data?.project),
        location: getVal(translation.location, data.location),
        area: getVal(translation.area, data.area),
        country: getVal(translation.country, data.country),
        city: getVal(translation.city, data.city),
      };
    }

    return null;
  };
  const trandata = language !== "en" ? getTranslatedData(item.id, language, item) : item;

  const tags = useMemo(() => {
    let combinedTags = item.tags || [];

    // Find and remove "Golden Visa" tag if it exists in item.tags
    const goldenVisaLabel = t("filter.label.others.goldenVisaText");
    const goldenVisaIndex = combinedTags.findIndex((tag) => tag.label === goldenVisaLabel);

    const goldenVisaTag =
      goldenVisaIndex !== -1
        ? { ...combinedTags.splice(goldenVisaIndex, 1)[0], isHiglighted: true }
        : item.goldenVisa
          ? { label: goldenVisaLabel, isHiglighted: true }
          : null;

    // Add "Online Exclusive" if applicable
    if (item.exclusive && showOnlineExclusiveFeatureFlag) {
      combinedTags = [{ label: t("filter.label.onlineExclusive"), isHiglighted: false }, ...combinedTags];
    }
    if (goldenVisaTag) {
      combinedTags = [goldenVisaTag, ...combinedTags];
    }
    return combinedTags;
  }, [item.tags, item.goldenVisa, item.exclusive, t]);
  return (
    <article className={cn(`group flex flex-col relative z-0 rounded-lg`, customClass)}>
      <div className="relative" style={{ height: isDesktop ? undefined : "calc(100% - 50px)" }}>
        <div className="relative rounded-lg overflow-hidden">
          <img
            key={`image-0`}
            alt={item?.images?.[0]?.title || ""}
            className="aspect-4-3 md:aspect-9-16 transition transform hover:duration-[6000ms] md:group-hover:scale-105 motion-reduce:transition-none motion-reduce:hover:transform-none w-full h-full object-cover object-[0_15%] md:object-center"
            src={contentfulImageLoader({ src: item?.images?.[0]?.url || transparentPixel, width: 400, quality: 65 })}
            srcSet={
              item?.images?.[0]?.url
                ? [300, 400, 600, 800].map((w) => `${contentfulImageLoader({ src: item.images[0].url, width: w, quality: 65 })} ${w}w`).join(", ")
                : undefined
            }
            sizes="(min-width: 1024px) 298px, calc(80vw)"
            loading="lazy"
            decoding="async"
          />
          <div className="absolute w-full h-full top-0 left-0 inset-0 img-overlay-2"></div>
        </div>

        <div className="absolute z-10 top-0 left-0 w-full h-full flex flex-row-reverse items-end justify-end gap-xs mt-auto md:flex-col md:items-start  text-white">
          <div className="md:w-full md:rounded-lg md:linear-gradient(181deg, rgba(0, 0, 0, 0.30) 1.08%, rgba(0, 0, 0, 0.00) 80.87%)">
            {(item?.logo || item?.projectLogo) && (
              <img
                src={item?.logo?.url || item?.projectLogo || ""}
                width={120}
                alt={item?.logo?.title || ""}
                className="mx-auto w-auto h-auto object-contain mb-l md:mt-l max-w-[120px]"
              />
            )}
          </div>
          <div
            onClick={() => {
              if (isDesktop) return;
              if (item?.cta && !item?.cta?.link?.url?.includes("#")) {
                propertyCardClick({
                  card_name: item?.title,
                  project_name: item?.title,
                  project_location: item?.location || "",
                  project_type: item?.unitType || "",
                  tag_name: item?.tags?.map((tag) => tag?.label).join(",") || "",
                  bedrooms_number: `${item?.bedrooms?.value}`,
                  property_category: item?.unitType || "",
                  property_indicative_price: formattedPrice || "",
                });
                router.push(projectResultPage ? finalUrl : finalUrlForCommunity);
                // router.push(item.cta.link.url);
              }
            }}
            className="carousel-projects-card flex flex-col flex-[2] gap-xs px-s pt-s pb-m text-s font-secondary-light h-1/2 justify-end md:h-auto  md:mt-auto md:w-full"
          >
            {tags && tags.length >= 0 && <CardTags tags={tags} isPostionAbsolute={false} />}
            <h3 className="font-medium text-m project-card-content">{trandata?.name || item?.title}</h3>
            {!!item?.location && <p>{trandata?.location}</p>}
            {!!item?.price && (
              <p className="hidden font-normal md:block">
                {(() => {
                  let labelText: any = fromLabel as unknown;
                  if (typeof fromLabel === "object" && fromLabel !== null && "props" in fromLabel && (fromLabel as any)?.props?.children) {
                    labelText = (fromLabel as any)?.props?.children as string;
                  }
                  if (typeof labelText === "string" && labelText.includes("AED")) {
                    const [fromPart, ...rest] = labelText.split("AED");
                    return (
                      <>
                        {fromPart}
                        <CurrencyIcon color="white" />
                        {rest.join("").trim()}
                      </>
                    );
                  } else if (locale?.split("-")?.[0] === "ar" && formattedPrice.includes("د.إ.")) {
                    const [fromPart, ...rest] = labelText.split("AED");
                    return (
                      <>
                        {fromPart?.replace(/AED|د\.إ\./g, " ")}
                        <CurrencyIcon color="white" />
                        {rest.join("").trim()}
                      </>
                    );
                  }
                  return fromLabel;
                })()}
              </p>
            )}
            {Object.keys(item?.bedrooms).length > 0 && item?.bedrooms?.value != "0" && (
              <p className="hidden md:block">
                {/* {item.bedrooms.value} {item.bedrooms.label} */}
                {formatBedroomValue(item?.bedrooms?.value)} {item.bedrooms.label}
                {item?.bedrooms?.value !== "" && item?.unitType ? `, ${item?.unitType}` : ""}
              </p>
            )}
            {item?.cta && !item?.cta?.link?.url?.includes("#") && (
              <div className="items-center hidden font-medium md:group-hover:flex mt-s">
                <Button
                  href={projectResultPage ? finalUrl : finalUrlForCommunity}
                  size={ButtonSize.SMALL}
                  theme={ButtonTheme.WHITE}
                  type="button"
                  variant={ButtonVariant.PRIMARY}
                  customClass="text-center !border-white hover:!border-0"
                >
                  {item.cta.label}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="relative z-20 p-s text-s md:hidden">
        {!!item.price && (
          <p className="font-medium">
            {(() => {
              let labelText: any = fromLabel as unknown;
              if (typeof fromLabel === "object" && fromLabel !== null && "props" in fromLabel && (fromLabel as any)?.props?.children) {
                labelText = (fromLabel as any)?.props?.children as string;
              }
              if (typeof labelText === "string" && labelText.includes("AED")) {
                const [fromPart, ...rest] = labelText.split("AED");
                return (
                  <>
                    {fromPart}
                    <CurrencyIcon color="black" />
                    {rest.join("").trim()}
                  </>
                );
              } else if (locale?.split("-")?.[0] === "ar" && formattedPrice.includes("د.إ.")) {
                const [fromPart, ...rest] = labelText.split("AED");
                return (
                  <>
                    {fromPart?.replace(/AED|د\.إ\./g, "")}
                    <CurrencyIcon color="black" />
                    {rest.join("").trim()}
                  </>
                );
              }
              return fromLabel;
            })()}
          </p>
        )}
        {Object.keys(item.bedrooms).length > 0 && (
          <p>
            {Number(item.bedrooms.value) > 0 && `${formatBedroomValue(item.bedrooms.value)}  ${item.bedrooms.label}`}
            {!!item.unitType && item.unitType !== "-" ? `, ${item.unitType}` : ""}
          </p>
        )}
      </div>
      {item.cta && !item?.cta?.link?.url?.includes("#") && (
        <Link
          onClick={() =>
            propertyCardClick({
              card_name: item.title,
              project_name: item.title,
              project_location: item.location || "",
              project_type: item.unitType || "",
              tag_name: item.tags?.map((tag) => tag.label).join(",") || "",
              bedrooms_number: `${item.bedrooms.value}`,
              property_category: item.unitType || "",
              property_indicative_price: formattedPrice || "",
            })
          }
          href={projectResultPage ? finalUrl : finalUrlForCommunity}
          // href={item.cta.link.url}
          className="absolute top-0 left-0 z-[5] w-full h-full"
        >
          <span className="sr-only">{item.cta.label}</span>
        </Link>
      )}
      {displayMapPin && !hideLocationIcon && (
        <button
          type="button"
          onClick={onPinSelected}
          onMouseUp={(e: any) => {
            if (e.button === 0 && !isMd) {
              window.scrollTo({ top: 0, behavior: "smooth" });
            }
          }}
          className="absolute z-10 top-s right-s"
        >
          <Icon name="geotag" size="24" className={item?.images?.[0]?.url.includes("placeholder") ? "stroke-black" : "stroke-white"} />
        </button>
      )}
    </article>
  );
}
